import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import api, { BACKEND_URL, inr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Camera, Image as ImageIcon, X, Upload, RotateCw, ShoppingBag, Sparkles,
  AlertCircle, ExternalLink, Trash2, Plus, Check, CheckCircle2, Info
} from "lucide-react";

// ────────────────────────────────────────────────────────────────────
// TryOnModal — full-screen Virtual Try-On shell.
// - Two modes: Live Camera (getUserMedia) + Photo (Take Photo / Upload)
// - Multi-product Selected Items panel with Remove / Change
// - Recommendations pulled from real catalog (View / Add-to-Try-On / Add-to-Cart)
// - Add Selected Items to Cart uses existing /api/cart/add
// - Provider adapter status displayed clearly — no fake AI results
// ────────────────────────────────────────────────────────────────────

const ACCEPT = "image/png,image/jpeg,image/webp";

export default function TryOnModal({ open, onClose, initialProduct }) {
  const { user, refreshCart } = useAuth();

  const [phase, setPhase] = useState("choose"); // choose | photo-choose | session
  const [mode, setMode] = useState(null);        // "live" | "photo"
  const [status, setStatus] = useState(null);    // provider status
  const [selected, setSelected] = useState([]);  // array of products (dedup by product_id)
  const [recs, setRecs] = useState([]);
  const [userPhoto, setUserPhoto] = useState(null); // local URL for photo mode
  const [uploading, setUploading] = useState(false);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const takePhotoRef = useRef(null);

  // Prime with the product the user clicked TRY IT ON from
  useEffect(() => {
    if (open && initialProduct) {
      setSelected((prev) => (prev.some(p => p.product_id === initialProduct.product_id) ? prev : [initialProduct]));
    }
    if (!open) resetAll();
  }, [open, initialProduct?.product_id]);

  // Fetch provider status
  useEffect(() => {
    if (!open) return;
    api.get("/tryon/status").then(r => setStatus(r.data)).catch(() => setStatus(null));
  }, [open]);

  // Load recommendations — anchored on the LAST selected product (most recent context)
  const anchorId = selected[selected.length - 1]?.product_id;
  useEffect(() => {
    if (!open || !anchorId) return;
    api.get(`/products/${anchorId}/recommendations?limit=8`)
      .then(r => setRecs((r.data || []).filter(p => !selected.some(s => s.product_id === p.product_id))))
      .catch(() => setRecs([]));
  }, [anchorId, selected, open]);

  // Camera lifecycle
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setCameraError(
        e?.name === "NotAllowedError"
          ? "Camera access is unavailable. You can upload a photo instead."
          : "Could not open camera on this device. You can upload a photo instead."
      );
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
  }, [stream]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Handlers
  const chooseLive = async () => {
    setMode("live");
    setPhase("session");
    await startCamera();
  };

  const choosePhoto = () => {
    setMode("photo");
    setPhase("photo-choose");
  };

  const onPhotoPicked = async (file) => {
    if (!file) return;
    if (!user) { toast.error("Please login first"); return; }
    setUploading(true);
    try {
      const localUrl = URL.createObjectURL(file);
      setUserPhoto(localUrl);
      // Also upload to server (kept for future AI provider calls)
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/tryon/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhase("session");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setUploading(false); }
  };

  const takeCameraPhoto = async () => {
    if (!user) { toast.error("Please login first"); return; }
    // Open a hidden input capture=camera
    if (takePhotoRef.current) takePhotoRef.current.click();
  };

  const resetAll = () => {
    stopCamera();
    setPhase("choose");
    setMode(null);
    setUserPhoto(null);
    setCameraError(null);
    setRecs([]);
    setSelected([]);
  };

  const closeModal = () => { resetAll(); onClose(); };

  // Multi-product state
  const addToTryOn = (p) => {
    setSelected(prev => prev.some(s => s.product_id === p.product_id) ? prev : [...prev, p]);
    toast.success(`${p.title} added to your look`);
  };
  const removeFromTryOn = (pid) => {
    setSelected(prev => prev.filter(s => s.product_id !== pid));
  };
  const changeItem = (pid) => {
    // "Change" = remove that slot; recommendations panel becomes the picker
    removeFromTryOn(pid);
    toast.info("Pick a new item from Complete Your Look");
  };

  const addAllToCart = async () => {
    if (!user) { toast.error("Please login first"); return; }
    if (!selected.length) return;
    try {
      for (const p of selected) {
        await api.post("/cart/add", {
          product_id: p.product_id, quantity: 1,
          size: p.sizes?.[0] || null, color: p.colors?.[0] || null,
        });
      }
      await refreshCart();
      toast.success(`Added ${selected.length} item${selected.length>1?"s":""} to your cart!`);
      closeModal();
    } catch (e) { toast.error("Could not add all items"); }
  };

  const addOneToCart = async (p) => {
    if (!user) { toast.error("Please login first"); return; }
    await api.post("/cart/add", {
      product_id: p.product_id, quantity: 1,
      size: p.sizes?.[0] || null, color: p.colors?.[0] || null,
    });
    await refreshCart();
    toast.success(`${p.title} added to cart`);
  };

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm text-white flex flex-col overflow-hidden" data-testid="tryon-modal">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#004BCA] to-[#FF4500]">
            <Sparkles size={14}/>
          </span>
          <div>
            <div className="font-display font-bold text-base leading-none">Virtual Try-On</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Powered by Stylefire</div>
          </div>
        </div>
        <button data-testid="tryon-close" onClick={closeModal} className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"><X size={18}/></button>
      </div>

      {/* Provider status banner — honest about shell mode */}
      {status && !status.provider_configured && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-100 px-4 sm:px-6 py-2 text-xs flex items-center gap-2 shrink-0" data-testid="tryon-shell-banner">
          <Info size={14} className="text-amber-300 shrink-0"/>
          <span><b>Preview mode:</b> AI provider not connected yet. Photo/camera and multi-product try-on UI is live — final AI-rendered result will appear once your admin enables a provider.</span>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {phase === "choose" && (
          <ChoosePhase onLive={chooseLive} onPhoto={choosePhoto} initialProduct={initialProduct}/>
        )}
        {phase === "photo-choose" && (
          <PhotoChoosePhase
            onTake={takeCameraPhoto}
            onUpload={() => fileRef.current?.click()}
            uploading={uploading}
          />
        )}
        {phase === "session" && (
          <SessionPhase
            mode={mode}
            userPhoto={userPhoto}
            videoRef={videoRef}
            cameraError={cameraError}
            selected={selected}
            recs={recs}
            initialProduct={initialProduct}
            status={status}
            onRemove={removeFromTryOn}
            onChange={changeItem}
            onAddToTryOn={addToTryOn}
            onAddOneToCart={addOneToCart}
            onSwitchToPhoto={() => { stopCamera(); choosePhoto(); }}
            onSwitchToLive={() => { setUserPhoto(null); setMode("live"); startCamera(); }}
          />
        )}
      </div>

      {/* Bottom action bar — Add Selected Items to Cart */}
      {phase === "session" && selected.length > 0 && (
        <div className="border-t border-white/10 bg-slate-950 px-4 sm:px-6 py-3 shrink-0" data-testid="tryon-action-bar">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="hidden sm:flex flex-1 items-center gap-2 text-xs text-slate-300">
              <CheckCircle2 size={14} className="text-emerald-400"/>
              {selected.length} item{selected.length>1?"s":""} in your look ·
              <span className="text-white font-semibold">{inr(selected.reduce((a,p)=>a+(p.price||0),0))}</span>
            </div>
            <button
              data-testid="tryon-add-all-cart"
              onClick={addAllToCart}
              className="btn-fire w-full sm:w-auto px-6 py-3 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2"
            >
              <ShoppingBag size={16}/> Add Selected Items to Cart
            </button>
          </div>
        </div>
      )}

      {/* Hidden inputs */}
      <input
        ref={fileRef} type="file" accept={ACCEPT} hidden
        data-testid="tryon-file-input"
        onChange={(e) => onPhotoPicked(e.target.files?.[0])}
      />
      <input
        ref={takePhotoRef} type="file" accept={ACCEPT} capture="user" hidden
        data-testid="tryon-camera-input"
        onChange={(e) => onPhotoPicked(e.target.files?.[0])}
      />
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

// ────────────── Sub-components ──────────────
function ChoosePhase({ onLive, onPhoto, initialProduct }) {
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
      {initialProduct && (
        <div className="flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-white/5 mb-6" data-testid="tryon-initial-product">
          <img src={initialProduct.images?.[0]} className="w-14 h-16 rounded-lg object-cover" alt=""/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">TRYING ON</div>
            <div className="font-medium truncate">{initialProduct.title}</div>
            <div className="text-xs text-slate-400">{inr(initialProduct.price)}</div>
          </div>
          <Check size={16} className="text-emerald-400"/>
        </div>
      )}
      <div className="text-center mb-8">
        <h2 className="font-display text-3xl sm:text-4xl font-bold">How would you like to try on?</h2>
        <p className="text-slate-400 text-sm mt-2">Pick a mode. You can build a full look with multiple products.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ChoiceCard testid="choice-live" icon={Camera} title="Live Camera" desc="See the product on you in real time using your camera." onClick={onLive} accent="#FF4500"/>
        <ChoiceCard testid="choice-photo" icon={ImageIcon} title="Try With Photo" desc="Take a photo now or upload one from your gallery." onClick={onPhoto} accent="#004BCA"/>
      </div>
    </div>
  );
}

function PhotoChoosePhase({ onTake, onUpload, uploading }) {
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
      <div className="text-center mb-8">
        <h2 className="font-display text-3xl font-bold">Choose your photo</h2>
        <p className="text-slate-400 text-sm mt-2">Best results: front-facing, well-lit, single person.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ChoiceCard testid="choice-take-photo" icon={Camera} title="Take Photo" desc="Use your camera now." onClick={onTake} accent="#FF4500"/>
        <ChoiceCard testid="choice-upload-photo" icon={Upload} title="Upload from Gallery" desc="Pick an existing photo." onClick={onUpload} accent="#004BCA"/>
      </div>
      {uploading && <div className="mt-6 text-center text-slate-300 text-sm animate-pulse">Uploading…</div>}
    </div>
  );
}

function ChoiceCard({ icon: Icon, title, desc, onClick, accent, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="group text-left border border-white/10 hover:border-white/25 rounded-3xl p-6 bg-white/5 hover:bg-white/[0.08] transition">
      <span className="grid place-items-center w-12 h-12 rounded-2xl mb-4" style={{background: `${accent}25`, color: accent}}><Icon size={22}/></span>
      <div className="font-display text-xl font-bold">{title}</div>
      <div className="text-sm text-slate-300 mt-1">{desc}</div>
    </button>
  );
}

function SessionPhase({
  mode, userPhoto, videoRef, cameraError, selected, recs, initialProduct, status,
  onRemove, onChange, onAddToTryOn, onAddOneToCart, onSwitchToPhoto, onSwitchToLive,
}) {
  const anchor = selected[selected.length - 1] || initialProduct;
  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 grid lg:grid-cols-[1fr_380px] gap-4 sm:gap-6">
      {/* Live/photo stage */}
      <div className="min-w-0">
        <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: "3/4" }}>
          {mode === "live" && !cameraError && (
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="tryon-video"/>
          )}
          {mode === "photo" && userPhoto && (
            <img src={userPhoto} alt="you" className="w-full h-full object-cover" data-testid="tryon-user-photo"/>
          )}
          {cameraError && (
            <div className="absolute inset-0 grid place-items-center p-6 text-center">
              <div>
                <AlertCircle size={36} className="mx-auto text-amber-400 mb-3"/>
                <p className="text-sm text-slate-200">{cameraError}</p>
                <button data-testid="tryon-fallback-photo" onClick={onSwitchToPhoto} className="btn-primary-sf mt-4 px-5 py-2 rounded-full text-sm font-semibold">Upload a Photo Instead</button>
              </div>
            </div>
          )}

          {/* Shell-mode overlay: shows the anchored product cutout so the user sees WHAT they picked,
              with an honest "AI overlay pending" tag. Not a fake AI result. */}
          {anchor && !cameraError && (
            <div className="absolute bottom-3 right-3 p-2 rounded-2xl bg-white/95 text-slate-900 shadow-2xl w-40" data-testid="tryon-overlay">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Currently trying</div>
              <img src={anchor.try_on_asset || anchor.images?.[0]} alt="" className="w-full h-24 object-cover rounded-lg mt-1"/>
              <div className="text-xs font-semibold mt-1 truncate">{anchor.title}</div>
              {!status?.provider_configured && (
                <div className="text-[10px] text-amber-600 mt-1 inline-flex items-center gap-1"><Info size={10}/> AI overlay pending</div>
              )}
            </div>
          )}
        </div>

        {/* Mode switch */}
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="tryon-switch-live" onClick={onSwitchToLive} className={`text-xs px-3 py-1.5 rounded-full border ${mode==="live"?"border-[#FF4500] bg-[#FF4500]/10 text-[#FFB088]":"border-white/15 text-slate-300 hover:bg-white/5"}`}><Camera size={12} className="inline mr-1"/>Live</button>
          <button data-testid="tryon-switch-photo" onClick={onSwitchToPhoto} className={`text-xs px-3 py-1.5 rounded-full border ${mode==="photo"?"border-[#004BCA] bg-[#004BCA]/10 text-blue-200":"border-white/15 text-slate-300 hover:bg-white/5"}`}><ImageIcon size={12} className="inline mr-1"/>Photo</button>
          <div className="ml-auto text-[11px] text-slate-500 hidden sm:block">Tip: multiple items stack in the panel →</div>
        </div>
      </div>

      {/* Right: Selected + Recommendations */}
      <div className="space-y-4">
        {/* SELECTED ITEMS */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="tryon-selected-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-sm inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400"/>Selected Items ({selected.length})</h3>
            {selected.length > 1 && (
              <button data-testid="tryon-try-all" className="text-[10px] uppercase tracking-widest text-[#FFB088] font-bold">Try All Together</button>
            )}
          </div>
          {selected.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center">Nothing selected yet.</div>
          ) : (
            <div className="space-y-2">
              {selected.map(p => (
                <div key={p.product_id} className="flex items-center gap-3 p-2 rounded-xl bg-white/5" data-testid={`selected-${p.product_id}`}>
                  <img src={p.images?.[0]} className="w-11 h-14 rounded-lg object-cover" alt=""/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    <div className="text-xs text-slate-400">{inr(p.price)} · {p.product_type || "item"}</div>
                  </div>
                  <button data-testid={`selected-change-${p.product_id}`} onClick={()=>onChange(p.product_id)} title="Change" className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/10"><RotateCw size={13}/></button>
                  <button data-testid={`selected-remove-${p.product_id}`} onClick={()=>onRemove(p.product_id)} title="Remove" className="w-8 h-8 grid place-items-center rounded-full hover:bg-red-500/20 text-red-300"><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* RECOMMENDATIONS */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="tryon-recs-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-sm inline-flex items-center gap-2"><Sparkles size={14} className="text-[#FFC85C]"/>Complete Your Look</h3>
            {anchor && <span className="text-[10px] uppercase tracking-widest text-slate-500 truncate max-w-[120px]">Based on {anchor.title}</span>}
          </div>
          {recs.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center">No recommendations. Ask your admin to link related products in the product edit page.</div>
          ) : (
            <div className="space-y-2">
              {recs.map(p => <RecCard key={p.product_id} p={p} onAddTryOn={()=>onAddToTryOn(p)} onAddCart={()=>onAddOneToCart(p)} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function RecCard({ p, onAddTryOn, onAddCart }) {
  return (
    <div className="flex gap-3 p-2 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`rec-${p.product_id}`}>
      <img src={p.images?.[0]} className="w-14 h-18 rounded-lg object-cover shrink-0" alt="" style={{height:72}}/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug line-clamp-2">{p.title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{inr(p.price)}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <a data-testid={`rec-view-${p.product_id}`} href={`/product/${p.product_id}`} target="_blank" rel="noreferrer"
            className="text-[10px] font-semibold px-2.5 py-1 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center gap-1">
            <ExternalLink size={10}/>View
          </a>
          <button data-testid={`rec-tryon-${p.product_id}`} onClick={onAddTryOn}
            className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#FF4500] hover:bg-[#e63e00] inline-flex items-center gap-1">
            <Plus size={10}/>Add to Try-On
          </button>
          <button data-testid={`rec-cart-${p.product_id}`} onClick={onAddCart}
            className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#004BCA] hover:bg-[#003ea8] inline-flex items-center gap-1">
            <ShoppingBag size={10}/>Cart
          </button>
        </div>
      </div>
    </div>
  );
}
